import { useState, useEffect } from "react";

import {
  Building2,
  Users,
  UserCheck,
  LayoutDashboard,
  FileText,
  Layers,
  Shield,
  Eye,
  Activity,
  Settings,
  LogOut,
  Video,
  BookOpen,
  Compass,
  SlidersHorizontal,
  PieChart,
  Timer,
  CalendarRange,
  FlaskConical,
  Radar,
  Map as MapIcon,
  Megaphone,
  Database,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import AdminProfileSettingsModal from "./AdminProfileSettingsModal";
import DataModal from "./DataModal";
import SignOutConfirmationModal from "./SignOutConfirmationModal";
import { logo } from "../../Assests/Images";
import TabsComponent from "../../Components/Tab";
import AdminAlAssessmentDefaultsContainer from "../../Container/AdminAlAssessmentDefaultsContainer";
import AdminAlAssessmentTestContainer from "../../Container/AdminAlAssessmentTestContainer";
import AdminBusinessHealthBriefingContainer from "../../Container/AdminBusinessHealthBriefingContainer";
import AdminCronSettingsContainer from "../../Container/AdminCronSettingsContainer";
import AdminGuidanceHubContainer from "../../Container/AdminGuidanceHubContainer";
import AdminIbHealthCheckContainer from "../../Container/AdminIbHealthCheckContainer";
import AdminLevelContainer from "../../Container/AdminLevelContainer";
import AdminMetricsConfigContainer from "../../Container/AdminMetricsConfigContainer";
import AdminOrganizationContainer from "../../Container/AdminOrganizationContainer";
import AdminOverviewContainer, {
  Organization,
} from "../../Container/AdminOverviewContainer";
import AdminPortfolioMapsContainer from "../../Container/AdminPortfolioMapsContainer";
import AdminReleaseNotesContainer from "../../Container/AdminReleaseNotesContainer";
import AdminSecurityContainer from "../../Container/AdminSecurityContainer";
import AdminSignalAdministrationContainer from "../../Container/AdminSignalAdministrationContainer";
import AdminTipsContainer from "../../Container/AdminTipsContainer";
import AdminUserManagementContainer from "../../Container/AdminUserManagementContainer";
import AdminVideoWorkspace from "../../Container/AdminVideoWorkspace";
import TemplateList from "../../Container/TemplateContainer/components/TemplateList";
import { useLogout } from "../../Hooks/useLogout";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchStatistics,
  fetchOrganizations,
  fetchAllUsers,
  fetchAdminActiveUsers,
} from "../../Store/Reducers/AdminSlice";
import {
  ROLES,
  ROLE_DISPLAY_NAMES,
  isRole,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

import "./style.scss";

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: "blue" | "green" | "purple" | "orange";
  onClick?: () => void;
  loading?: boolean;
}

const StatCard = ({
  title,
  value,
  icon: Icon,
  color,
  onClick,
  loading,
}: StatCardProps) => {
  const colorClasses: Record<"blue" | "green" | "purple" | "orange", string> = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
  };

  return (
    <div
      className={`bg-white p-6 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 ${
        onClick
          ? "cursor-pointer hover:shadow-md hover:border-gray-300 hover:-translate-y-0.5"
          : ""
      } ${loading ? "opacity-75" : ""} relative`}
      onClick={onClick}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg z-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
        </div>
      )}
      <div className="flex items-center">
        <div className={`${colorClasses[color]} p-3 rounded-lg shadow-sm`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        {onClick && (
          <div className="ml-auto">
            <Eye className="h-4 w-4 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
};

export type { Organization };

const CONFIG_SUB_TAB_IDS = [
  "templates",
  "levels",
  "videos",
  "guidance",
  "playbook-hub",
  "cron",
  "al-assessment-defaults",
  "al-assessment-test",
  "portfolio-maps",
  "signal-administration",
] as const;

type ConfigSubTabId = (typeof CONFIG_SUB_TAB_IDS)[number];

const isConfigSubTab = (tab: string): tab is ConfigSubTabId =>
  CONFIG_SUB_TAB_IDS.includes(tab as ConfigSubTabId);

const resolveActiveTabFromSearchParams = (
  searchParams: URLSearchParams
): string => {
  const tabFromQuery = searchParams.get("tab") || "overview";
  const subtabFromQuery = searchParams.get("subtab");

  if (isConfigSubTab(tabFromQuery)) {
    return tabFromQuery;
  }
  if (tabFromQuery === "config") {
    return subtabFromQuery && isConfigSubTab(subtabFromQuery)
      ? subtabFromQuery
      : "templates";
  }
  return tabFromQuery;
};

const AdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const logoutSession = useLogout();

  // Get user role for permissions
  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const reduxUserRole = user?.user?.role;
  const storageUserRole = loggedInUser?.role;
  const userRole = reduxUserRole || storageUserRole;

  const isSuperAdmin = isRole(userRole, ROLES.IZBA_ADMIN);

  // Get initial tab from URL query parameter
  const tabFromQuery = resolveActiveTabFromSearchParams(searchParams);
  const {
    statistics,
    organizations: apiOrganizations,
    allUsers: apiAllUsers,
    activeUsers: apiActiveUsers,
    loading: adminLoading,
  } = useAppSelector((state: any) => state.admin);

  // Check if statistics are loading initially
  const isStatisticsLoading = adminLoading && !statistics;

  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [isActiveUsersModalOpen, setIsActiveUsersModalOpen] = useState(false);
  const [isProfileSettingsModalOpen, setIsProfileSettingsModalOpen] =
    useState(false);
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeUsersLoading, setActiveUsersLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const toggleOrgModal = async () => {
    // If data doesn't exist, fetch it first
    if (!apiOrganizations || apiOrganizations.length === 0) {
      setOrgLoading(true);
      try {
        await dispatch(fetchOrganizations() as any);
      } finally {
        setOrgLoading(false);
      }
    }
    setIsOrgModalOpen(!isOrgModalOpen);
  };

  const toggleUsersModal = async () => {
    // If data doesn't exist, fetch it first
    if (!apiAllUsers || apiAllUsers.length === 0) {
      setUsersLoading(true);
      try {
        await dispatch(fetchAllUsers() as any);
      } finally {
        setUsersLoading(false);
      }
    }
    setIsUsersModalOpen(!isUsersModalOpen);
  };

  const toggleActiveUsersModal = () =>
    setIsActiveUsersModalOpen(!isActiveUsersModalOpen);

  const handleActiveUsersClick = async () => {
    // If data doesn't exist, fetch it first
    if (!apiActiveUsers || apiActiveUsers.length === 0) {
      setActiveUsersLoading(true);
      try {
        await dispatch(fetchAdminActiveUsers("active") as any);
      } finally {
        setActiveUsersLoading(false);
      }
    }
    toggleActiveUsersModal();
  };

  const configSubTabs = [
    { id: "templates", label: "Templates", icon: <FileText size={18} /> },
    { id: "levels", label: "Levels", icon: <Layers size={18} /> },
    { id: "videos", label: "Videos", icon: <Video size={18} /> },
    {
      id: "guidance",
      label: "Guidance Library",
      icon: <BookOpen size={18} />,
    },
    {
      id: "playbook-hub",
      label: "Playbook Hub",
      icon: <Compass size={18} />,
    },
    { id: "cron", label: "Cron Settings", icon: <Timer size={18} /> },
    {
      id: "al-assessment-defaults",
      label: "AL Assessment Defaults",
      icon: <CalendarRange size={18} />,
    },
    {
      id: "al-assessment-test",
      label: "AL Assessment Test",
      icon: <FlaskConical size={18} />,
    },
    {
      id: "portfolio-maps",
      label: "Portfolio Maps",
      icon: <MapIcon size={18} />,
    },
    {
      id: "signal-administration",
      label: "Signal Administration",
      icon: <Radar size={18} />,
    },
  ];

  const primaryAdminTabs = [
    { id: "overview", label: "Overview", icon: <LayoutDashboard size={18} /> },
    {
      id: "organizations",
      label: "Organizations",
      icon: <Building2 size={18} />,
    },
    {
      id: "release-notes",
      label: "Release notes",
      icon: <Megaphone size={18} />,
    },
    {
      id: "user-management",
      label: "User Management",
      icon: <Users size={18} />,
    },
    {
      id: "config",
      label: "Config",
      icon: <SlidersHorizontal size={18} />,
    },
    {
      id: "metrics-config",
      label: "Metrics Config",
      icon: <PieChart size={18} />,
    },
    {
      id: "ib-health-check",
      label: "IB Health Check",
      icon: <Activity size={18} />,
    },
    {
      id: "bh-briefings",
      label: "BH Digests",
      icon: <Database size={18} />,
    },
    { id: "security", label: "Security", icon: <Shield size={18} /> },
  ];

  const orgColumns = [
    {
      key: "organization",
      title: "ORGANIZATION",
      render: (value: string) => <span className="org-name">{value}</span>,
    },
    {
      key: "domain",
      title: "DOMAIN",
      render: (value: string) => <span className="org-domain">{value}</span>,
    },
    {
      key: "users_count",
      title: "USERS",
    },
    {
      key: "status",
      title: "STATUS",
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
          {value}
        </span>
      ),
    },
  ];

  const getStatusBadgeClasses = (status: string) => {
    if (status === "active") {
      return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize";
    }
    if (status === "inactive") {
      return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 capitalize";
    }
    return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 capitalize";
  };

  const allUsersColumns = [
    {
      key: "user",
      title: "USER",
      render: (value: string) => <span className="user-name">{value}</span>,
    },
    {
      key: "organization",
      title: "ORGANIZATION",
      render: (value: string) => <span className="user-org">{value}</span>,
    },
    {
      key: "role",
      title: "ROLE",
      render: (value: string) => {
        const normalizedRole = normalizeRoleName(value);
        const displayName =
          normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
            ? ROLE_DISPLAY_NAMES[
                normalizedRole as keyof typeof ROLE_DISPLAY_NAMES
              ]
            : value;
        return <span>{displayName}</span>;
      },
    },
    {
      key: "status",
      title: "STATUS",
      render: (value: string) => (
        <span className={getStatusBadgeClasses(value)}>{value}</span>
      ),
    },
  ];

  const activeUserColumns = [
    {
      key: "user",
      title: "USER",
      render: (value: string) => <span className="user-name">{value}</span>,
    },
    {
      key: "organization",
      title: "ORGANIZATION",
      render: (value: string) => <span className="user-org">{value}</span>,
    },
    {
      key: "role",
      title: "ROLE",
      render: (value: string) => {
        const normalizedRole = normalizeRoleName(value);
        const displayName =
          normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
            ? ROLE_DISPLAY_NAMES[
                normalizedRole as keyof typeof ROLE_DISPLAY_NAMES
              ]
            : value;
        return <span>{displayName}</span>;
      },
    },
    {
      key: "status",
      title: "STATUS",
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
          {value}
        </span>
      ),
    },
    {
      key: "last_login_at",
      title: "LAST LOGIN",
      render: (value?: string) => (
        <span>{value ? new Date(value).toLocaleDateString("en-GB") : "-"}</span>
      ),
    },
  ];

  const handleOrganizationRefresh = () => {
    dispatch(fetchOrganizations() as any);
    dispatch(fetchStatistics() as any);
  };

  const metricData: Array<{
    title: string;
    value: number;
    onClick: () => void;
    icon: React.ComponentType<{ className?: string }>;
    color: "blue" | "green" | "purple" | "orange";
    loading: boolean;
  }> = [
    {
      title: "Total Organizations",
      value: statistics?.totalOrganizations ?? 0,
      onClick: toggleOrgModal,
      icon: Building2,
      color: "blue" as const,
      loading: orgLoading || isStatisticsLoading,
    },
    {
      title: "Total Users",
      value: statistics?.totalUsers ?? 0,
      onClick: toggleUsersModal,
      icon: Users,
      color: "green" as const,
      loading: usersLoading || isStatisticsLoading,
    },
    {
      title: "Active Users",
      value: statistics?.activeUsers ?? 0,
      onClick: handleActiveUsersClick,
      icon: UserCheck,
      color: "purple" as const,
      loading: activeUsersLoading || isStatisticsLoading,
    },
  ];

  const organizations: {
    label: string;
    value: string;
    tenant_schema_name: string;
  }[] = apiOrganizations.map((org: Organization) => ({
    label: org.organization,
    value: org.organization,
    tenant_schema_name: org.tenant_schema_name,
  }));

  const [activeAdminFilter, setActiveAdminFilter] = useState(tabFromQuery);
  const [activeTemplateTab, setActiveTemplateTab] = useState<
    "izba_provided" | "custom"
  >("izba_provided");

  // Fetch data on-demand when tab changes

  useEffect(() => {
    dispatch(fetchStatistics() as any);
  }, []);

  useEffect(() => {
    if (activeAdminFilter === "overview") {
      // Overview needs statistics and organizations
      dispatch(fetchOrganizations() as any);
    } else if (activeAdminFilter === "organizations") {
      // Organizations tab needs organizations data
      dispatch(fetchOrganizations() as any);
    } else if (activeAdminFilter === "release-notes") {
      dispatch(fetchOrganizations() as any);
    } else if (activeAdminFilter === "ib-health-check") {
      dispatch(fetchOrganizations() as any);
    } else if (activeAdminFilter === "bh-briefings") {
      dispatch(fetchOrganizations() as any);
    } else if (activeAdminFilter === "metrics-config") {
      dispatch(fetchOrganizations() as any);
    } else if (activeAdminFilter === "user-management") {
      // User Management tab needs users data
      if (!apiOrganizations || apiOrganizations.length === 0) {
        dispatch(fetchOrganizations() as any);
      }
      dispatch(fetchAllUsers() as any);
    }
  }, [activeAdminFilter, dispatch]);

  const activePrimaryTab = isConfigSubTab(activeAdminFilter)
    ? "config"
    : activeAdminFilter;

  const handlePrimaryTabChange = (tabId: string) => {
    if (tabId === "config") {
      const nextSubTab = isConfigSubTab(activeAdminFilter)
        ? activeAdminFilter
        : "templates";
      setActiveAdminFilter(nextSubTab);
      setSearchParams({ tab: "config", subtab: nextSubTab });
      return;
    }
    setActiveAdminFilter(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleConfigSubTabChange = (subTabId: string) => {
    setActiveAdminFilter(subTabId);
    setSearchParams({ tab: "config", subtab: subTabId });
  };

  // Update active tab when URL query parameter changes
  useEffect(() => {
    setActiveAdminFilter(resolveActiveTabFromSearchParams(searchParams));
  }, [searchParams]);

  const handleTemplateTabChange = (tab: "izba_provided" | "custom") => {
    setActiveTemplateTab(tab);
  };

  const handleOpenProfileSettings = () => {
    setIsProfileSettingsModalOpen(true);
  };

  const handleCloseProfileSettings = () => {
    setIsProfileSettingsModalOpen(false);
  };

  const handleLogoutClick = () => {
    setIsSignOutModalOpen(true);
  };

  const handleCloseSignOutModal = () => {
    setIsSignOutModalOpen(false);
  };

  const handleConfirmLogout = async () => {
    setLogoutLoading(true);
    await logoutSession();
  };

  // Get user data for header
  const reduxUser = user?.user;
  const displayName =
    reduxUser?.full_name || loggedInUser?.fullName || "ExecBridge Admin";
  const displayEmail =
    reduxUser?.email || loggedInUser?.email || "admin@izba.ai";
  const roleDisplayName = "Super Admin";

  const headerTitle = (
    <>
      <div className="admin-header flex items-center gap-4">
        <div className="flex-shrink-0 flex items-center justify-center overflow-hidden">
          <img
            src={logo}
            alt="Logo"
            className="w-full h-full object-contain p-1.5"
          />
        </div>
        <div className="flex flex-col">
          <h1 className="m-0 text-xl font-semibold text-gray-900">
            Multi-Tenant Admin
          </h1>
          <p className="m-0 text-sm text-gray-600 font-normal">
            Organization Management Dashboard
          </p>
        </div>
      </div>
    </>
  );

  const headerActions = (
    <div className="flex items-center">
      <div className="flex items-center gap-5 py-1.5 pl-3 rounded-lg">
        <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-md bg-green-50">
          <Shield size={18} className="text-green-600" strokeWidth={2} />
          <span className="text-sm text-gray-900 font-semibold">
            {roleDisplayName}
          </span>
        </div>
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="text-sm font-semibold text-gray-900 leading-tight truncate max-w-[200px]">
            {displayName}
          </div>
          <div className="text-xs text-gray-500 leading-tight truncate max-w-[200px]">
            {displayEmail}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-1 border-l border-gray-200 pl-3">
          <button
            className="flex items-center justify-center p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all duration-200 hover:scale-105 active:scale-95"
            onClick={handleOpenProfileSettings}
            aria-label="Settings"
            title="Profile Settings"
          >
            <Settings size={18} strokeWidth={2} />
          </button>
          <button
            className="flex items-center justify-center p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-all duration-200 hover:scale-105 active:scale-95"
            onClick={handleLogoutClick}
            aria-label="Logout"
            title="Sign Out"
          >
            <LogOut size={18} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page__header-wrapper w-full">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6">
            <div className="z-[1] bg-white px-0 py-3">
              <div className="flex min-h-10 items-center justify-between gap-3 md:gap-4">
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className="truncate text-lg font-bold leading-tight text-gray-900">
                      {headerTitle}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {headerActions}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-[90rem] mx-auto px-4 sm:px-6">
        <div className="admin-container">
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {metricData.map((metric, index) => (
                <StatCard
                  key={index}
                  title={metric.title}
                  value={metric.value}
                  icon={metric.icon}
                  color={metric.color}
                  onClick={metric.onClick}
                  loading={metric.loading}
                />
              ))}
            </div>
          </div>
        </div>
        <DataModal
          isOpen={isOrgModalOpen}
          toggle={toggleOrgModal}
          title="All Organizations"
          data={apiOrganizations}
          columns={orgColumns}
        />
        <DataModal
          isOpen={isUsersModalOpen}
          toggle={toggleUsersModal}
          title="All Users"
          data={apiAllUsers}
          columns={allUsersColumns}
        />
        <DataModal
          isOpen={isActiveUsersModalOpen}
          toggle={toggleActiveUsersModal}
          title="Active Users"
          data={apiActiveUsers || []}
          columns={activeUserColumns}
        />

        <AdminProfileSettingsModal
          isOpen={isProfileSettingsModalOpen}
          toggle={handleCloseProfileSettings}
          onSignOut={handleLogoutClick}
        />

        <SignOutConfirmationModal
          isOpen={isSignOutModalOpen}
          toggle={handleCloseSignOutModal}
          onConfirm={handleConfirmLogout}
          loading={logoutLoading}
        />

        <div className="admin-navigation mb-6">
          <div className="w-full min-w-0 overflow-x-auto">
            <TabsComponent
              tabs={primaryAdminTabs}
              activeTab={activePrimaryTab}
              onTabChange={handlePrimaryTabChange}
              variant="tab-segmented"
            />
          </div>
          {isConfigSubTab(activeAdminFilter) && (
            <div className="admin-config-subtabs mt-4 min-w-0 border-b border-gray-200">
              <TabsComponent
                tabs={configSubTabs}
                activeTab={activeAdminFilter}
                onTabChange={handleConfigSubTabChange}
                variant="tab-underline"
                className="mb-0 admin-config-subtabs__tabs library-nav-scroll"
              />
            </div>
          )}
        </div>
        {activeAdminFilter === "overview" && (
          <AdminOverviewContainer
            recentOrganizations={apiOrganizations}
            loading={adminLoading}
          />
        )}

        {activeAdminFilter === "organizations" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminOrganizationContainer
              organizations={apiOrganizations}
              onRefresh={handleOrganizationRefresh}
              loading={adminLoading}
            />
          </div>
        )}
        {activeAdminFilter === "release-notes" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminReleaseNotesContainer
              organizations={apiOrganizations}
              initialOrgId={searchParams.get("org") || ""}
            />
          </div>
        )}
        {activeAdminFilter === "user-management" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminUserManagementContainer
              users={apiAllUsers}
              organizations={organizations}
              loading={adminLoading}
            />
          </div>
        )}
        {activeAdminFilter === "levels" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminLevelContainer loading={adminLoading} />
          </div>
        )}
        {activeAdminFilter === "videos" && (
          <div className="admin-page__content admin-page__content--videos bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
            <AdminVideoWorkspace />
          </div>
        )}
        {activeAdminFilter === "guidance" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminTipsContainer loading={adminLoading} />
          </div>
        )}
        {activeAdminFilter === "playbook-hub" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
            <AdminGuidanceHubContainer />
          </div>
        )}
        {activeAdminFilter === "cron" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminCronSettingsContainer />
          </div>
        )}
        {activeAdminFilter === "al-assessment-defaults" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminAlAssessmentDefaultsContainer />
          </div>
        )}
        {activeAdminFilter === "al-assessment-test" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminAlAssessmentTestContainer />
          </div>
        )}
        {activeAdminFilter === "portfolio-maps" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
            <AdminPortfolioMapsContainer />
          </div>
        )}
        {activeAdminFilter === "signal-administration" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminSignalAdministrationContainer />
          </div>
        )}
        {activeAdminFilter === "ib-health-check" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminIbHealthCheckContainer organizations={apiOrganizations} />
          </div>
        )}
        {activeAdminFilter === "bh-briefings" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <AdminBusinessHealthBriefingContainer
              organizations={apiOrganizations}
            />
          </div>
        )}
        {activeAdminFilter === "security" && <AdminSecurityContainer />}
        {activeAdminFilter === "metrics-config" && (
          <AdminMetricsConfigContainer
            organizations={apiOrganizations}
            loading={adminLoading}
          />
        )}
        {activeAdminFilter === "templates" && (
          <div className="admin-page__content bg-white rounded-lg shadow-sm border border-gray-200">
            <TemplateList
              maxShortTemplates={null}
              maxLongTemplates={null}
              isSuperAdmin={isSuperAdmin}
              activeTab={activeTemplateTab}
              onTabChange={handleTemplateTabChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
